import { Link } from 'react-router-dom';
import type { ExamSchedule } from '@/types';
import { formatDday } from '@/lib/datetime';

export interface UpcomingExam extends ExamSchedule {
  studentName: string;
}

interface UpcomingExamsCardProps {
  exams: UpcomingExam[];
}

function ddayBadgeClass(tone: 'past' | 'soon' | 'far'): string {
  if (tone === 'soon') return 'bg-accent-100 text-accent-800';
  if (tone === 'past') return 'bg-background-200 text-foreground-500';
  return 'bg-secondary-100 text-secondary-900';
}

export default function UpcomingExamsCard({ exams }: UpcomingExamsCardProps) {
  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
          <i className="ri-calendar-event-line"></i>
        </span>
        <h2 className="text-base font-bold text-foreground-950">다가오는 시험 일정</h2>
      </div>

      {exams.length === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 py-10">
          <i className="ri-calendar-line text-2xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600">예정된 시험이 없어요.</p>
        </div>
      ) : (
        <div className="mt-5 flex flex-col gap-3">
          {exams.map((e) => {
            const dday = formatDday(e.end_date ?? e.start_date);
            return (
              <Link
                key={e.id}
                to={`/students/${e.student_id}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3 transition hover:border-primary-300"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-file-list-3-line"></i>
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground-900">
                      {e.studentName} · {e.title || '시험'}
                    </p>
                    <p className="truncate text-xs text-foreground-500">{e.scope || '범위 미정'}</p>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  {dday && (
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${ddayBadgeClass(
                        dday.tone,
                      )}`}
                    >
                      {dday.label}
                    </span>
                  )}
                  <span className="text-[11px] text-foreground-500 whitespace-nowrap">
                    {e.start_date ?? '-'}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}